import * as React from 'react'
import axios from 'axios'
import { Box, Modal, ModalDialog, ModalClose, Typography } from '@mui/joy'
import type { IRoute } from '@numeo/types-core'
import type { TruckLocationHistoryRow } from './TruckHistory'
import { sortTruckHistoryByTimestampAsc } from './utils/truckHistoryUtils'
import { SelectedTrucksMapSection } from './components/SelectedTrucksMapSection'
import { SelectedTrucksSidebar } from './components/SelectedTrucksSidebar'

interface SelectedTrucksMapModalProps {
    open: boolean
    trucks: TruckLocationHistoryRow[]
    onClose: () => void
    singleProjectName: string | null
    singleAssignedLoadId: string | null
}

export const SelectedTrucksMapModal: React.FC<SelectedTrucksMapModalProps> = ({ open, trucks, onClose, singleProjectName, singleAssignedLoadId }) => {
    const sortedTrucks = React.useMemo(() => sortTruckHistoryByTimestampAsc(trucks), [trucks])

    const [activeVin, setActiveVin] = React.useState<string | null>(null)
    const [focusedRow, setFocusedRow] = React.useState<TruckLocationHistoryRow | null>(null)
    const [loadRouteStops, setLoadRouteStops] = React.useState<IRoute[] | null>(null)
    const [loadRouteLoading, setLoadRouteLoading] = React.useState(false)
    const [loadRouteError, setLoadRouteError] = React.useState<string | null>(null)

    const handleActiveVinChange = React.useCallback((vin: string | null) => {
        setActiveVin(vin)
        setFocusedRow(null)
    }, [])

    const handleStopClick = React.useCallback((row: TruckLocationHistoryRow) => {
        setActiveVin(row.vin)
        setFocusedRow(row)
    }, [])

    React.useEffect(() => {
        if (!singleAssignedLoadId || !singleProjectName) {
            setLoadRouteStops(null)
            setLoadRouteError(null)
            setLoadRouteLoading(false)
            return
        }

        let cancelled = false

        const fetchLoadRoute = async () => {
            try {
                setLoadRouteLoading(true)
                setLoadRouteError(null)

                const response = await axios.get('/gluon/truck-history/load-route', {
                    params: {
                        projectName: singleProjectName,
                        assignedLoad: singleAssignedLoadId,
                    },
                })

                if (cancelled) return

                const route: IRoute[] | null = Array.isArray(response.data?.route) ? (response.data.route as IRoute[]) : null

                setLoadRouteStops(route)
            } catch (error) {
                if (cancelled) return
                const message = error instanceof Error ? error.message : 'Failed to load route for selected load'
                setLoadRouteStops(null)
                setLoadRouteError(message)
            } finally {
                if (!cancelled) {
                    setLoadRouteLoading(false)
                }
            }
        }

        fetchLoadRoute()

        return () => {
            cancelled = true
        }
    }, [singleAssignedLoadId, singleProjectName])

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                sx={{
                    maxWidth: '1200px',
                    width: '100%',
                    maxHeight: '90vh',
                    height: '90vh',
                    p: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: 'lg',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 'md',
                }}
            >
                <ModalClose />
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.surface' }}>
                    <Typography level="title-sm">Selected Truck Locations ({trucks.length})</Typography>
                </Box>

                <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden', flexDirection: { xs: 'column', sm: 'row' } }}>
                    <SelectedTrucksSidebar
                        trucks={sortedTrucks}
                        activeVin={activeVin}
                        onActiveVinChange={handleActiveVinChange}
                        onStopClick={handleStopClick}
                    />

                    <Box
                        sx={{
                            flex: 1,
                            position: 'relative',
                            overflow: 'hidden',
                            height: '100%',
                            minHeight: 400,
                            borderLeft: { xs: 'none', sm: '1px solid' },
                            borderTop: { xs: '1px solid', sm: 'none' },
                            borderColor: 'divider',
                        }}
                    >
                        <SelectedTrucksMapSection
                            trucks={sortedTrucks}
                            activeVin={activeVin}
                            focusedRow={focusedRow}
                            loadRouteStops={loadRouteStops}
                            loadRouteLoading={loadRouteLoading}
                            loadRouteError={loadRouteError}
                        />
                    </Box>
                </Box>
            </ModalDialog>
        </Modal>
    )
}
